import { JSX, useMemo } from "react";
import { Col, Divider, Row } from "antd";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import { getTierAvailableYear } from "../../../datasource/TierProgramDatasource";

export interface YearOption {
  label: string;
  value: string;
}

export interface StartFromOption {
  label: string;
  description: string;
  value: string;
}

interface Props {
  year: string;
  setYear: (value: string) => void;
  nextYears: YearOption[];
  startFrom: string;
  setStartFrom: (value: string) => void;
  startFromOptions: StartFromOption[];
  onNext: () => void;
}

function Step1({
  year,
  setYear,
  nextYears,
  startFrom,
  setStartFrom,
  startFromOptions,
  onNext,
}: Props): JSX.Element {
  const navigate = useNavigate();

  const company = JSON.parse(localStorage.getItem("profile") || "null")?.company;

  const { data, isLoading: isAvailableYearLoading } = useQuery(
    ["tier-available-year", company],
    () => getTierAvailableYear({ company }),
    { enabled: !!company, refetchOnWindowFocus: false },
  );
  const availableYears = data?.responseData?.availableYears;
  const lastYear = data?.responseData?.latestProgram?.year;

  const yearsToRender: YearOption[] = useMemo(() => {
    return (availableYears ?? []).map((y: any) => {
      if (typeof y === "number" || typeof y === "string") {
        return { label: `ปี ${y}`, value: String(y) };
      }
      const raw = y.year ?? y.value ?? y.label;
      return { label: `ปี ${raw}`, value: String(raw) };
    });
  }, [availableYears]);

  if(isAvailableYearLoading){
    return <div>Loading...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", marginTop: 24 }}>
      <Text fontSize={16} fontWeight={600} color="Text2">
        ปีสิทธิประโยชน์
      </Text>
      <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
        ปีที่มีชุดสิทธิประโยชน์อยู่แล้วจะไม่อยู่ในรายการนี้
      </Text>
      <div style={{ display: "flex", flexDirection: "row", gap: 8, margin: "24px 0" }}>
        {(yearsToRender ?? []).map((y) => (
          <div
            key={y.value}
            onClick={() => setYear(y.value)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: `1px solid ${year === y.value ? "#1D4ED8" : "#E5E7EB"}`,
              cursor: "pointer",
              backgroundColor: year === y.value ? "#EFF6FF" : "#FFFFFF",
            }}
          >
            <Text
              fontSize={14}
              fontWeight={year === y.value ? 700 : 500}
              color={year === y.value ? "primary" : "Text2"}
            >
              {y.label}
            </Text>
          </div>
        ))}
      </div>
      <Text fontSize={16} fontWeight={600} color="Text2">
        เริ่มต้นจาก
      </Text>
      <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
        เลือกว่าจะเริ่มจากชุดเดิมหรือเขียนใหม่ทั้งหมด แก้ต่อได้ในขั้นถัดไป
      </Text>
      <Row style={{ margin: "12px 0" }} gutter={8}>
        {startFromOptions.map((opt) => (
          <Col span={12} key={opt.value}>
            <div
              onClick={() => setStartFrom(opt.value)}
              style={{
                padding: "16px 20px",
                borderRadius: 8,
                border: `1px solid ${startFrom === opt.value ? "#1D4ED8" : "#E5E7EB"}`,
                cursor: "pointer",
                backgroundColor: startFrom === opt.value ? "#EFF6FF" : "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <Text
                fontSize={16}
                fontWeight={startFrom === opt.value ? 700 : 500}
                color={startFrom === opt.value ? "primary" : "Text2"}
              >
                {opt.label}
              </Text>
              <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
                {opt.description}
              </Text>
            </div>
          </Col>
        ))}
      </Row>
      <Text fontFamily="Sarabun" fontSize={14} fontWeight={400} color="Text3">
        ปี {year} ยังไกลกว่าปีถัดไป ร้านจะยังไม่เห็นจนกว่าปีนั้นจะขยับเข้ามา
      </Text>
      <Divider />
      <Row justify="start" gutter={8}>
        <Col>
          <Button
            typeButton="border-primary"
            title="ยกเลิก"
            style={{ width: "auto" }}
            onClick={() => navigate(-1)}
          />
        </Col>
        <Col>
          <Button
            typeButton={year ? "primary" : "disabled"}
            title="ถัดไป"
            style={{ width: "auto" }}
            onClick={onNext}
            disabled={!year}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Step1;
